<template>
  <div class="settings-users-edit">
    <div class="page__header">
      <div class="page__title">
        <router-link
          :to="{ name: 'settings-users', params: { id: selectedCompany } }"
          class="page__back-link"
        >
          <arrow-button
            class="page__back-link-icon"
            :rotated180="true"
          ></arrow-button>
        </router-link>
        <div class="page__title-text">
          {{ $t('settings.users.pages.edit.title') }}
        </div>
      </div>
    </div>
    <div
      class="settings-users-edit__form settings-users-edit__form--loading"
      v-if="isRolesLoading"
    >
      <loader></loader>
    </div>
    <div class="settings-users-edit__form" v-else>
      <div class="settings-users-edit__form-fields">
        <div class="settings-users-edit__form-field">
          <text-input
            v-model:value="userData.cell_phone"
            :mask="$options.PHONE_MASK"
            :placeholder="
              $t('settings.users.pages.edit.fields.phone-placeholder')
            "
            :title="$t('settings.users.pages.edit.fields.phone')"
            :disabled="true"
            :with-information="true"
          >
            <template #information>
              <change-phone-email-hint></change-phone-email-hint>
            </template>
          </text-input>
        </div>
        <div class="settings-users-edit__form-field">
          <text-input
            v-model:value="userData.email"
            :title="$t('settings.users.pages.edit.fields.email')"
            :placeholder="
              $t('settings.users.pages.edit.fields.email-placeholder')
            "
            :disabled="true"
            :with-information="true"
          >
            <template #information>
              <change-phone-email-hint></change-phone-email-hint>
            </template>
          </text-input>
        </div>
        <div class="settings-users-edit__form-field">
          <text-input
            v-model:value="userData.first_name"
            :title="$t('settings.users.pages.edit.fields.firstname')"
            :placeholder="
              $t('settings.users.pages.edit.fields.firstname-placeholder')
            "
            :disabled="true"
          >
          </text-input>
        </div>
        <div class="settings-users-edit__form-field">
          <text-input
            v-model:value="userData.last_name"
            :title="$t('settings.users.pages.edit.fields.lastname')"
            :placeholder="
              $t('settings.users.pages.edit.fields.lastname-placeholder')
            "
            :disabled="true"
          ></text-input>
        </div>
        <div class="settings-users-edit__form-field">
          <custom-select
            mode="big"
            :title="$t('settings.users.pages.edit.fields.role')"
            :placeholder="
              $t('settings.users.pages.edit.fields.role-placeholder')
            "
            v-model:value="userData.role"
            :options="availableRoles"
            value-key="id"
            :has-default-value="false"
          ></custom-select>
        </div>
      </div>
      <div class="settings-users-edit__form-actions">
        <primary-button
          class="settings-users-edit__form-action"
          :disabled="v$.$silentErrors.length > 0 || isUserUpdating"
          :label="$t('settings.users.pages.edit.actions.edit')"
          @click="editUser"
        ></primary-button>
      </div>
    </div>
    <success-notification
      ref="userEditedSuccessNotification"
      :text="$t('settings.users.notifications.user-edited')"
    ></success-notification>
  </div>
</template>

<script>
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import Pagination from '@vue-app/components/base/control/Pagination.vue'
import SearchInput from '@vue-app/components/base/input/SearchInput.vue'
import { mapState } from 'vuex'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import UserItem from '@vue-app/components/company/settings/users/UserItem.vue'
import Loader from '@vue-app/components/base/Loader.vue'
import ArrowButton from '@vue-app/components/base/button/ArrowButton.vue'
import TextInput from '@vue-app/components/base/input/TextInput.vue'
import CustomSelect from '@vue-app/components/base/input/CustomSelect.vue'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import ChangePhoneEmailHint from '@vue-app/components/user/profile/ChangePhoneEmailHint.vue'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'

export default {
  name: 'SettingsUserEdit',
  components: {
    SuccessNotification,
    ChangePhoneEmailHint,
    CustomSelect,
    TextInput,
    ArrowButton,
    Loader,
    PrimaryButton,
  },
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      userData: {},
      isRolesLoading: true,
      isUserUpdating: false,
      availableRoles: [],
    }
  },
  props: {
    id: [String, Number],
    userId: [String, Number],
  },
  validations() {
    return {
      userData: {
        role: {
          required,
        },
      },
    }
  },
  PHONE_MASK: '00000 000 00 00',
  computed: {
    ...mapState(['selectedCompany']),
  },
  watch: {},
  created() {
    this.startupPage()
  },
  methods: {
    async startupPage() {
      await this.loadRoles()
      await this.loadUser()
    },
    async loadRoles() {
      this.isRolesLoading = true

      try {
        let _rolesResponse = await CompaniesApi.getCompanyRoles(
          this.selectedCompany
        )
        this.availableRoles = _rolesResponse.data.map((_role) => ({
          id: _role.role_slug,
          name: `roles.${_role.role_slug}`,
        }))
      } catch (e) {
      } finally {
        this.isRolesLoading = false
      }
    },
    async loadUser() {
      let _usersResponse = await CompaniesApi.getUsers(this.selectedCompany)
      let _currentUser = _usersResponse.data.find(
        (u) => parseInt(u.user_id) === parseInt(this.userId)
      )
      this.userData = {
        ..._currentUser,
        role: _currentUser.role_slug,
      }
    },
    async editUser() {
      this.isUserUpdating = true

      let isValid = await this.v$.$validate()

      if (!isValid) {
        this.isUserUpdating = false
        return false
      }

      try {
        await CompaniesApi.updateCompanyUserRole(this.selectedCompany, {
          user_id: this.userData.user_id,
          role: this.userData.role,
        })

        await this.$refs.userEditedSuccessNotification.show()
        await this.$router.push({
          name: 'settings-users',
          params: {
            ...this.$route.params,
          },
        })
      } catch (e) {
      } finally {
        this.isUserUpdating = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.settings-users-edit {
  &__form {
    background: #ffffff;
    border-radius: 22px;
    padding: 60px 50px 74px;
    width: 100%;
    max-width: 390px;
    margin-top: 20px;

    &--loading {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &-fields {
      margin-bottom: 40px;
    }

    &-field {
      margin-bottom: 30px;

      &-hint {
        width: 100%;
        font-weight: 500;
        font-size: 10px;
        line-height: 14px;
        color: #b9c4d8;
        margin-top: 5px;
        flex-wrap: wrap;
      }

      &--row {
        display: flex;
        align-items: stretch;
        flex-wrap: wrap;
      }

      &-small {
        width: 95px;
        margin-right: 26px;
        flex-shrink: 0;
        flex-grow: 0;
      }

      &-big {
        width: calc(100% - 121px);
        flex-shrink: 1;
        flex-grow: 1;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    &-action {
      padding: 25px 25px;
    }
  }
}
</style>
